import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import { PsrRecordType } from '@nxsys/types/models/revenueFilings/psr';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetLeaverToSubmitPSRParams,
  GetLeaverToSubmitPSRResponse,
  GetPayrollByIdParams,
  GetPayrollByIdResponse,
  GetPayrollToSubmitPSRParams,
  GetPayrollToSubmitPSRResponse,
  GetPSRListParams,
  GetPSRListResponse,
  ResubmitLeaverPsrByIdParams,
  ResubmitPSRParams,
  SubmitLeaverPSRParams,
  SubmitLeaverPSRResponse,
  SubmitPSRPayrollByIdsParams,
  SubmitPSRPayrollByIdsResponse
} from '../types';

export const getPsrRecords = createAsyncThunk<
  GetPSRListResponse,
  GetPSRListParams
>(
  'revenue/getPsrRecords',
  async (params: GetPSRListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.revenueFilings.psr, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPsrRecordsAll = createAsyncThunk<
  GetPSRListResponse,
  GetPSRListParams
>(
  'revenue/getPsrRecordsAll',
  async (params: GetPSRListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.revenueFilings.psr, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPsrRecordsError = createAsyncThunk<
  PsrRecordType[],
  GetPSRListParams
>(
  'revenue/getPsrRecordsError',
  async (params: GetPSRListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.revenueFilings.psr, {
        params: {
          ...params,
          isSuccess: false,
        },
      });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const resubmitPsrRecords = createAsyncThunk<
  PsrRecordType[],
  ResubmitPSRParams
>(
  'revenue/resubmitPsrRecords',
  async (params: ResubmitPSRParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.revenueFilings.resubmitPSR,
        {
          ids: params.ids,
        },
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const resubmitLeaverPSRRecords = createAsyncThunk<
  PsrRecordType[],
  ResubmitPSRParams
>(
  'revenue/resubmitLeaverPSRRecords',
  async (params: ResubmitPSRParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.employment.resubmitLeaver,
        {
          ids: params.ids,
        },
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPayrollToSubmitPSR = createAsyncThunk<
  GetPayrollToSubmitPSRResponse,
  GetPayrollToSubmitPSRParams
>(
  'revenue/getPayrollToSubmitPSR',
  async (params: GetPayrollToSubmitPSRParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.report.payroll.mainV2, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getLeaverToSubmitPSR = createAsyncThunk<
  GetLeaverToSubmitPSRResponse,
  GetLeaverToSubmitPSRParams
>(
  'revenue/getLeaverToSubmitPSR',
  async (params: GetLeaverToSubmitPSRParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.employment.mainIreland, {
        params,
      });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const submitLeaverPSR = createAsyncThunk<
  SubmitLeaverPSRResponse,
  SubmitLeaverPSRParams
>(
  'revenue/submitLeaverPSR',
  async (params: SubmitLeaverPSRParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.employment.leaver, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPayrollById = createAsyncThunk<
  GetPayrollByIdResponse,
  GetPayrollByIdParams
>(
  'revenue/getPayrollById',
  async (params: GetPayrollByIdParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`${endPoints.payroll.main}/${params.id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const submitPSRByPayrollIds = createAsyncThunk<
  SubmitPSRPayrollByIdsResponse,
  SubmitPSRPayrollByIdsParams
>(
  'revenue/submitPSRByPayrollIds',
  async (params: SubmitPSRPayrollByIdsParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.revenueFilings.psr, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const resubmitLeaverPsrById = createAsyncThunk<
  PsrRecordType[],
  ResubmitLeaverPsrByIdParams
>(
  'revenue/resubmitLeaverPsrById',
  async (params: ResubmitLeaverPsrByIdParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        `${endPoints.employment.resubmitLeaver}/${params.id}`,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const revenueActs = {
  getPsrRecords,
  getPsrRecordsAll,
  getPsrRecordsError,
  resubmitPsrRecords,
  resubmitLeaverPSRRecords,
  getPayrollToSubmitPSR,
  getLeaverToSubmitPSR,
  submitLeaverPSR,
  getPayrollById,
  submitPSRByPayrollIds,
  resubmitLeaverPsrById,
};
